import { useState } from 'react';
// import { ChevronLeft } from 'lucide-react';
import Navbar from '../components/Navbar.tsx';

type Language = 'en' | 'fr' | 'ar';

const translations = {
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: February 2026',
    backToHome: 'Back to Home',
    sections: [
      {
        title: 'Information We Collect',
        content: 'We collect information you provide directly to us, such as when you create an account, request a demo, or contact us. This may include your name, email address, phone number, company information, and any other information you choose to provide.'
      },
      {
        title: 'How We Use Your Information',
        content: 'We use the information we collect to: provide, maintain, and improve our services; process transactions; send you technical notices and support messages; communicate with you about products, services, and events; and monitor and analyze trends and usage.'
      },
      {
        title: 'Information Sharing',
        content: 'We do not sell, trade, or otherwise transfer your personal information to outside parties except as described in this policy. We may share information with trusted partners who assist us in operating our platform, conducting business, or serving users.'
      },
      {
        title: 'Data Security',
        content: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.'
      },
      {
        title: 'Your Rights',
        content: 'You have the right to access, update, or delete your personal information. You may also opt out of certain communications from us. Contact us to exercise these rights.'
      },
      {
        title: 'Contact Us',
        content: 'If you have questions about this Privacy Policy, please contact us at privacy@karia.tn or through our website contact form.'
      }
    ]
  },
  fr: {
    title: 'Politique de Confidentialité',
    lastUpdated: 'Dernière mise à jour : Février 2026',
    backToHome: 'Retour à l\'accueil',
    sections: [
      {
        title: 'Informations que nous collectons',
        content: 'Nous collectons les informations que vous nous fournissez directement, par exemple lorsque vous créez un compte, demandez une démo ou nous contactez. Cela peut inclure votre nom, adresse e-mail, numéro de téléphone, informations sur l\'entreprise et toute autre information que vous choisissez de fournir.'
      },
      {
        title: 'Comment nous utilisons vos informations',
        content: 'Nous utilisons les informations que nous collectons pour : fournir, maintenir et améliorer nos services ; traiter les transactions ; vous envoyer des avis techniques et des messages d\'assistance ; communiquer avec vous sur les produits, services et événements ; surveiller et analyser les tendances et l\'utilisation.'
      },
      {
        title: 'Partage d\'informations',
        content: 'Nous ne vendons, n\'échangeons ou ne transférons pas vos informations personnelles à des tiers, sauf comme décrit dans cette politique. Nous pouvons partager des informations avec des partenaires de confiance qui nous aident à exploiter notre plateforme.'
      },
      {
        title: 'Sécurité des données',
        content: 'Nous mettons en place des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos informations personnelles contre l\'accès non autorisé, la modification, la divulgation ou la destruction.'
      },
      {
        title: 'Vos droits',
        content: 'Vous avez le droit d\'accéder, de mettre à jour ou de supprimer vos informations personnelles. Vous pouvez également vous désabonner de certaines communications. Contactez-nous pour exercer ces droits.'
      },
      {
        title: 'Nous contacter',
        content: 'Si vous avez des questions sur cette Politique de Confidentialité, veuillez nous contacter à privacy@karia.tn ou via notre formulaire de contact.'
      }
    ]
  },
  ar: {
    title: 'سياسة الخصوصية',
    lastUpdated: 'آخر تحديث: فبراير 2026',
    backToHome: 'العودة للرئيسية',
    sections: [
      {
        title: 'المعلومات التي نجمعها',
        content: 'نجمع المعلومات التي تقدمها لنا مباشرة، مثل عندما تنشئ حساباً أو تطلب عرضاً توضيحياً أو تتصل بنا. قد يشمل ذلك اسمك وعنوان بريدك الإلكتروني ورقم هاتفك ومعلومات الشركة وأي معلومات أخرى تختار تقديمها.'
      },
      {
        title: 'كيف نستخدم معلوماتك',
        content: 'نستخدم المعلومات التي نجمعها لـ: توفير وصيانة وتحسين خدماتنا؛ معالجة المعاملات؛ إرسال إشعارات تقنية ورسائل دعم؛ التواصل معك حول المنتجات والخدمات والأحداث؛ مراقبة وتحليل الاتجاهات والاستخدام.'
      },
      {
        title: 'مشاركة المعلومات',
        content: 'لا نبيع أو نتداول أو ننقل معلوماتك الشخصية لأطراف خارجية إلا كما هو موضح في هذه السياسة. قد نشارك المعلومات مع شركاء موثوقين يساعدوننا في تشغيل منصتنا.'
      },
      {
        title: 'أمان البيانات',
        content: 'ننفذ تدابير أمنية تقنية وتنظيمية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التغيير أو الكشف أو التدمير.'
      },
      {
        title: 'حقوقك',
        content: 'لديك الحق في الوصول إلى معلوماتك الشخصية أو تحديثها أو حذفها. يمكنك أيضاً إلغاء الاشتراك في بعض المراسلات منا. اتصل بنا لممارسة هذه الحقوق.'
      },
      {
        title: 'اتصل بنا',
        content: 'إذا كان لديك أسئلة حول سياسة الخصوصية هذه، يرجى الاتصال بنا على privacy@karia.tn أو من خلال نموذج الاتصال على موقعنا.'
      }
    ]
  }
};

const Privacy = () => {
  const [lang, setLang] = useState<Language>('fr');
  const t = translations[lang];
  const isRTL = lang === 'ar';

  return (
    <div className="min-h-screen bg-white text-slate-900" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navbar currentLang={lang} onLanguageChange={setLang} />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-slate max-w-none" dir={isRTL ? 'rtl' : 'ltr'}>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">{t.title}</h1>
          <p className="text-slate-600 mb-8">{t.lastUpdated}</p>

          <div className="space-y-8">
            {t.sections.map((section, index) => (
              <section key={index} className="border-b border-slate-100 pb-6 last:border-b-0">
                <h2 className="text-xl font-semibold text-slate-900 mb-4">{section.title}</h2>
                <p className="text-slate-700 leading-relaxed">{section.content}</p>
              </section>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-slate-50 mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center">
          <div className="flex items-center justify-center gap-2 text-slate-600 mb-4">
            <span>© 2026 karia.tn. Tous droits réservés.</span>
          </div>
          <div className="flex items-center justify-center gap-6 text-sm">
            <a href="/" className="text-slate-600 hover:text-slate-900 transition-colors">{t.backToHome}</a>
            <a href="/terms" className="text-slate-600 hover:text-slate-900 transition-colors">Conditions</a>
            <a href="/security" className="text-slate-600 hover:text-slate-900 transition-colors">Sécurité</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;